import { createApi } from "@reduxjs/toolkit/query/react";

import { CustomerStatus } from "@/constants/client.constants";
import { IAdmin } from "@/interfaces/admin.interface";
import { ICustomerUser } from "@/interfaces/customer-user.interface";
import { ICustomer } from "@/interfaces/customer.interface";
import { IEmployee } from "@/interfaces/employee.interface";
import { IWorkForceUserEntity, RoleEnum } from "@/interfaces/globals";
import { IManager } from "@/interfaces/manager.interface";
import { ISubContractor } from "@/interfaces/subcontractor.interface";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["users", "employee"],
  endpoints: (builder) => ({
    // get list of users according to the usertype
    getUserList: builder.query<
      | GeneralResponse<ISubContractor[]>
      | GeneralResponse<IEmployee[]>
      | GeneralResponse<IManager[]>
      | GeneralResponse<IAdmin[]>,
      Partial<{
        userType: string;
        status: string;
        filter?: string;
        approvedStatus?: "PENDING" | "APPROVED";
      }>
    >({
      query: ({ status, userType, filter, approvedStatus }) =>
        `/superadmin/workforce/${userType}${status ? `?status=${status}` : ""}${filter ? filter : ""}${approvedStatus ? `&approvedStatus=${approvedStatus}` : ""}`,
      providesTags: ["users", "employee"],
    }),
    // list of user according to the  workspace
    getUsersByWS: builder.query<
      | GeneralResponse<ISubContractor[]>
      | GeneralResponse<IEmployee[]>
      | GeneralResponse<IManager[]>
      | GeneralResponse<IAdmin[]>,
      Partial<{
        userType: string;
        status: string;
        filter?: string;
        approvedStatus?: "PENDING" | "APPROVED";
      }>
    >({
      query: ({ userType, status, filter, approvedStatus }) =>
        `workspace/${getWorkspace()}/workforce/${userType}${status ? `?status=${status}` : ""}${filter ? filter : ""}${approvedStatus ? `&approvedStatus=${approvedStatus}` : ""}`,
      providesTags: ["users", "employee"],
    }),

    getSingleUser: builder.query<IAdmin, { userType: string; userId: string }>({
      query: ({ userType, userId }) =>
        `/superadmin/workforce/${userType}/${userId}`,
      providesTags: ["users", "employee"],
      transformResponse: (response: APIResponse<IAdmin>) => response?.data,
    }),

    getSingleCustomerUser: builder.query<ICustomerUser, { userId: string }>({
      query: ({ userId }) =>
        `/workspace/${getWorkspace()}/customer/user/${userId}`,
      providesTags: ["users", "employee"],
      transformResponse: (response: APIResponse<ICustomerUser>) =>
        response?.data,
    }),

    alterCustomer: builder.mutation<object, unknown>({
      query: ({ data, userId }) => ({
        url: `/workspace/${getWorkspace()}/customer/${userId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["employee", "users"],
    }),

    getSingleCustomer: builder.query<ICustomer, { userId: string }>({
      query: ({ userId }) => `/workspace/${getWorkspace()}/customer/${userId}`,
      providesTags: ["users", "employee"],
      transformResponse: (response: APIResponse<ICustomer>) => response?.data,
    }),

    deleteUser: builder.mutation<unknown, unknown>({
      query: ({ userType, userId }) => ({
        url: `/superadmin/workforce/${userType}/${userId}`,
        method: "DELETE",
        providesTags: ["users", "employee"],
      }),
    }),

    toggleUserStatusWs: builder.mutation<
      unknown,
      { userType: string; userId: string; status: string; data?: object }
    >({
      query: ({ userType, userId, status, data }) => ({
        url: `/workspace/${getWorkspace()}/workforce/${userType}/${userId}/${status}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["employee"],
    }),

    toggleUserStatus: builder.mutation<
      unknown,
      { userType: string; userId: string; status: string }
    >({
      query: ({ userType, userId, status }) => ({
        url: `/superadmin/workforce/${userType}/${userId}/${status}`,
        method: "POST",
      }),
      invalidatesTags: ["employee"],
    }),

    getSingleByWs: builder.query<
      ISubContractor | IManager,
      { userType: string; userId: string }
    >({
      query: ({ userType, userId }) =>
        `workspace/${getWorkspace()}/workforce/${userType}/${userId}`,
      providesTags: ["users", "employee"],
      transformResponse: (response: APIResponse<ISubContractor | IManager>) =>
        response?.data,
    }),

    addUser: builder.mutation<
      object,
      Partial<{ data: object; userType: string }>
    >({
      query: ({ data, userType }) => ({
        url: `/superadmin/workforce/${userType}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["users"],
    }),

    alterUser: builder.mutation<
      object,
      Partial<{ data: object; userType: string; id: string }>
    >({
      query: ({ data, userType, id }) => ({
        url: `/superadmin/workforce/${userType}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["employee", "users"],
    }),

    alterCustomerUser: builder.mutation<
      object,
      Partial<{ data: object; id: string }>
    >({
      query: ({ data, id }) => ({
        url: `/workspace/${getWorkspace()}/customer/user/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["employee", "users"],
    }),

    addUserByWs: builder.mutation<
      object,
      Partial<{ data: object; userType: string }>
    >({
      query: ({ data, userType }) => ({
        url: `workspace/${getWorkspace()}/workforce/${userType}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["users"],
    }),

    alterUserByWs: builder.mutation<
      object,
      Partial<{
        data: object;
        userType: string;
        userId: string;
        method?: string;
      }>
    >({
      query: ({ data, userType, userId, method }) => ({
        url: `workspace/${getWorkspace()}/workforce/${userType}/${userId ? userId : "ss"}`,
        method: method?.toUpperCase() || "PUT",
        body: data,
      }),
      invalidatesTags: ["users"],
    }),

    // to register the user account
    employeeRegistration: builder.mutation<unknown, object>({
      query: (data) => ({
        url: "workspace/register",
        method: "POST",
        body: data,
      }),
      transformResponse: (response: APIResponse<IEmployee>) => response?.data,
      invalidatesTags: ["users"],
    }),

    //To get all mangers, users and admins
    getAllWSUsers: builder.query<IWorkForceUserEntity[], string>({
      query: () => `/workspace/${getWorkspace()}/workforce/user`,
      providesTags: ["users", "employee"],
      transformResponse: (response: APIResponse<IWorkForceUserEntity[]>) =>
        response?.data,
    }),
    getAllWorforceUser: builder.query<
      IWorkForceUserEntity[],
      { filter?: string; role?: RoleEnum[]; status?: CustomerStatus }
    >({
      query: ({ filter, role, status = CustomerStatus.ACTIVE }) => {
        const params = new URLSearchParams();
        role?.forEach((r) => params.append("role", r));

        return `/workspace/${getWorkspace()}/workforce/user/all?${params}${filter ? filter : ""}&status=${status}`;
      },
    }),
  }),
});

export const {
  useAlterCustomerUserMutation,
  useGetSingleCustomerQuery,
  useGetSingleCustomerUserQuery,
  useGetUserListQuery,
  useGetSingleUserQuery,
  useGetSingleByWsQuery,
  useAlterUserByWsMutation,
  useToggleUserStatusWsMutation,
  useAlterUserMutation,
  useToggleUserStatusMutation,
  useAddUserMutation,
  useAddUserByWsMutation,
  useDeleteUserMutation,
  useGetUsersByWSQuery,
  useEmployeeRegistrationMutation,
  // useAddUserPermissionMutation,
  useAlterCustomerMutation,
  useGetAllWSUsersQuery,
  useGetAllWorforceUserQuery,
} = userApi;
